import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, FolderKanban, CheckSquare, Users, LogOut,
  Menu, X, ChevronRight, CalendarClock
} from 'lucide-react';

const NAV = [
  { to: '/os/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/os/intake', icon: CalendarClock, label: 'Intake' },
  { to: '/os/projects', icon: FolderKanban, label: 'Proyectos' },
  { to: '/os/tasks', icon: CheckSquare, label: 'Tareas' },
  { to: '/os/team', icon: Users, label: 'Equipo' },
];

export default function OSLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/os/login'); };

  const roleLabel = {
    admin: 'Administrador', designer: 'Diseñador', procurement: 'Compras',
    logistics: 'Logística', client: 'Cliente', supplier: 'Proveedor',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 flex flex-col transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: '#2D2D2D' }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b" style={{ borderColor: '#3D3D3D' }}>
          <div className="flex items-center gap-3">
            <img
              src="/images/logo-cr-home.png"
              alt="CR Home" className="h-7 w-auto object-contain brightness-0 invert"
            />
            <span className="text-sm font-light tracking-widest" style={{ color: '#C9A66B' }}>OS</span>
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-6 px-3 space-y-1">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to} to={to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-light transition-all duration-200 ${isActive ? 'bg-white/10' : 'hover:bg-white/5'}`
              }
              style={({ isActive }) => ({ color: isActive ? '#C9A66B' : '#A0A0A0' })}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} />
                  <span>{label}</span>
                  {isActive && <ChevronRight size={14} className="ml-auto" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t" style={{ borderColor: '#3D3D3D' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium" style={{ backgroundColor: '#C9A66B', color: '#2D2D2D' }}>
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-light truncate" style={{ color: '#E8E3D8' }}>{user?.name}</p>
              <p className="text-xs" style={{ color: '#C9B8A8' }}>{roleLabel[user?.role] || user?.role}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs font-light tracking-wide w-full px-3 py-2 rounded-lg transition-colors hover:bg-white/5"
            style={{ color: '#A0A0A0' }}
          >
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-6 border-b" style={{ borderColor: '#E8E3D8', backgroundColor: '#FFFFFF' }}>
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} style={{ color: '#2D2D2D' }}>
            <Menu size={22} />
          </button>
          <div />
          <div className="flex items-center gap-3">
            <span className="text-xs font-light tracking-wide hidden sm:block" style={{ color: '#C9B8A8' }}>{user?.email}</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
